import { faCloudArrowDown, faEye, faEyeSlash, faFolderOpen, faPeopleGroup, faSend, faWrench } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Card, CardContent, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useContext, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CommunityGroupMemberStatus } from "../../api/dtos/dtos";
import AuthContext from "../../contexts/auth/AuthContext";
import MapContext from "../../contexts/data/MapContext";
import UserGroupsContext from "../../contexts/data/UserGroupsContext";
import isCurrentGroupAdmin from "../../functions/user";
import useGetAllCommunityGroupMembers from "../../hooks/data/useGetAllCommunityGroupMembers";

const MembershipCard = () => {
    let navigate = useNavigate();
    //
    // -- Context --
    const { data: userGroupsConfig } = useContext(UserGroupsContext);
    const { filter } = useContext(MapContext);
    const { user } = useContext(AuthContext);

    //
    // -- Params
    const { species: speciesName } = useParams();

    const isGroupAdmin = useMemo(() => {
        return isCurrentGroupAdmin(userGroupsConfig, user, filter);
    }, [userGroupsConfig, user, filter, isCurrentGroupAdmin]);

    // Generate the community group members request
    const communityGroupMembersRequest = useMemo(() => {
        if (filter.bySubmitter?.communityGroupId && isGroupAdmin){
            return {
                communityGroupId: filter.bySubmitter.communityGroupId,
            };
        }
    }, [filter, isGroupAdmin]);

    // Get community group members requests
    const { data: communityGroupMembers }  = useGetAllCommunityGroupMembers(
        communityGroupMembersRequest, 
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );

    return (
        <Card variant='outlined'>
            <CardContent>
                <Grid container spacing={1} alignItems={"center"}>
                    <Grid item xs={12}>
                        <Box display="flex" alignItems={"center"}>
                            <FontAwesomeIcon icon={faPeopleGroup} style={{ fontSize: '1.2rem', paddingRight: "5px" }} />
                            <Typography paddingLeft={"5px"} fontSize={24}>
                                Membership - Invite people, and manage the membership of your group
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                <Box sx={{padding: "10px"}} />

                { isGroupAdmin && (
                    <>
                        <Button 
                            variant="text" 
                            startIcon={<FontAwesomeIcon icon={faWrench} />}
                            onClick={() => navigate("/" + speciesName + "/managegroupmembers")}
                        >
                            Manage Group Members
                        </Button>
                        <Button 
                            variant="text" 
                            startIcon={<FontAwesomeIcon icon={faFolderOpen} />}
                            onClick={() => navigate("/" + speciesName + "/managegroupdocuments")}
                        >
                            Manage Group Documents
                        </Button>
                        <Button 
                            variant="text" 
                            startIcon={<FontAwesomeIcon icon={faSend} />}
                            onClick={() => navigate("/" + speciesName + "/invitegroupmembers")}
                        >
                            Invite Group Members
                        </Button>
                    </>
                )}

                <Box sx={{padding: "10px"}} />

                <Table>
                    <TableHead>
                        <TableRow sx={{backgroundColor: '#f0f0f0'}}>
                            <TableCell align="left">
                                <Typography fontSize={16} fontWeight={600}>
                                    Name
                                </Typography>
                            </TableCell>
                            <TableCell align="left">
                                <Typography fontSize={16} fontWeight={600}>
                                    Email
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography fontSize={16} fontWeight={600}>
                                    Download Rights
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography fontSize={16} fontWeight={600}>
                                    View Rights
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            isGroupAdmin && communityGroupMembers?.communityGroupMembers && (
                                communityGroupMembers?.communityGroupMembers
                                ?.filter(x => x.communityGroupStatusId == CommunityGroupMemberStatus.Accepted)
                                ?.map(
                                communityGroupMember => {
                                    return(
                                        <TableRow key={communityGroupMember.userId}>
                                            <TableCell>
                                                {communityGroupMember.user?.fullName}
                                            </TableCell>
                                            <TableCell>
                                                {communityGroupMember.email}
                                            </TableCell>
                                            <TableCell align="center">
                                                {
                                                    communityGroupMember.downloadRights 
                                                        ? <FontAwesomeIcon icon={faCloudArrowDown} /> 
                                                        : ''
                                                }
                                            </TableCell>
                                            <TableCell align="center">
                                                {
                                                    communityGroupMember.viewRights 
                                                        ? <FontAwesomeIcon icon={faEye} /> 
                                                        : <FontAwesomeIcon icon={faEyeSlash} />
                                                }
                                            </TableCell>
                                        </TableRow>
                                    );
                                }
                            ))
                        }
                        { !isGroupAdmin && userGroupsConfig?.communityGroups?.find(
                                communityGroup => communityGroup.communityGroupId === filter.bySubmitter.communityGroupId
                            )?.communityGroupMembers?.filter(cgm => cgm.communityGroupStatusId === CommunityGroupMemberStatus.Accepted
                            )?.map(
                                communityGroupMember => {
                                    return(
                                        <TableRow key={communityGroupMember.userId}>
                                            <TableCell>
                                                {communityGroupMember.user.fullName}
                                            </TableCell>
                                            <TableCell>
                                                {communityGroupMember.email}
                                            </TableCell>
                                            <TableCell align="center">
                                                {
                                                    communityGroupMember.downloadRights 
                                                        ? <FontAwesomeIcon icon={faCloudArrowDown} /> 
                                                        : ''
                                                }
                                            </TableCell>
                                            <TableCell align="center">
                                                {
                                                    communityGroupMember.viewRights 
                                                        ? <FontAwesomeIcon icon={faEye} /> 
                                                        : <FontAwesomeIcon icon={faEyeSlash} />
                                                }
                                            </TableCell>
                                        </TableRow>
                                    );
                                }
                            )
                        }
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    )
}

export default MembershipCard